import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { limitToCents } from '../../lib/amount';
import axios from 'axios';
import {
  maxBirthDateISO,
  optionLabel,
  type CatalogOption,
  type FormFieldDef,
} from '../../lib/workflowForm';
import { getApiErrorMessage } from '../../lib/apiError';
import { toText } from '../../lib/toText';
import { formatBytes, type UploadProgress } from '../../lib/uploadProgress';
import workflowService from '../../services/workflow.service';
import { readerLocale } from '../../i18n/language';
import { colors } from '../../styles/theme';
import CountryPicker from '../CountryPicker';
import DocumentExpiryCapture, {
  type ExpirySavedState,
  type SaveValidityBody,
} from './DocumentExpiryCapture';

/** Server-side pointer written into `case_data` for file fields. */
export interface UploadedFilePointer {
  document_id: string;
  storage_path: string;
  file_hash: string;
  size_bytes: number;
  mime_type: string;
}

interface Props {
  field: FormFieldDef;
  value: unknown;
  /** Current value of the sibling field referenced by
   *  `field.options_filter_field`. Used to lazy-fetch options for
   *  cascading selects (e.g. state filtered by country). */
  parentValue?: unknown;
  enabled: boolean;
  required: boolean;
  error: string | null;
  /** True once the user has interacted with this field (blurred a
   *  text input, picked from a select, or after they attempted to
   *  submit). The component renders the validation error only when
   *  this is true — keeps the initial form clean instead of red. */
  wasTouched?: boolean;
  /** Fired the moment the user commits an interaction (blur on text
   *  inputs, change on selects/radios/checkboxes). The parent flips
   *  `wasTouched` to true next render. */
  onTouch?: () => void;
  /** Per-field review state from the agent's last pass. Drives the
   *  client UX: `approved` locks the input (read-only + lock icon);
   *  `rejected` keeps it editable but surfaces the agent's reason
   *  inline so the client knows what to fix. Undefined / 'pending'
   *  is the normal behavior. */
  reviewStatus?: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string | null;
  onChange: (value: unknown) => void;
  /** Required when the field is a `file`/`signature`. The parent
   *  closes over caseId + fieldKey + service call so this component
   *  stays free of service / routing concerns. */
  onUploadFile?: (
    file: File,
    options: {
      onProgress: (progress: UploadProgress) => void;
      signal: AbortSignal;
    },
  ) => Promise<UploadedFilePointer>;
  /** Initial expiry captured on the current document (for file fields
   *  with an uploaded `pointer`). When absent / both null, the input
   *  renders empty. Skipping these props hides the expiry UI entirely. */
  documentExpiry?: ExpirySavedState;
  /** Closure that PATCHes the expiry on the current document. The
   *  parent provides caseId via closure. Skipping this hides the
   *  expiry UI even when a pointer exists (e.g. signature fields
   *  don't carry expiry semantics). */
  onSaveDocumentExpiry?: (
    documentId: string,
    body: SaveValidityBody,
  ) => Promise<ExpirySavedState>;
  /** When true, the input renders as read-only (with a visual badge)
   *  even though `enabled` stays true so the field is still included
   *  in the submit payload. Used for CP-autofilled address fields. */
  readOnly?: boolean;
  /** When provided, overrides a `text` field to render as a `select`
   *  using the given list of string options. Used for the `colonia`
   *  field when the SEPOMEX CP lookup returns a colonias list. */
  optionsOverride?: string[];
}

const SUPPORTED_CONTROLS = new Set([
  'text', 'textarea', 'number', 'currency', 'date', 'datetime',
  'phone', 'email', 'rfc', 'curp',
  'select', 'radio', 'checkbox',
  'file', 'signature',
]);
const FILE_CONTROLS = new Set(['file', 'signature']);
const TEXT_CONTROLS = new Set(['text', 'phone', 'email', 'rfc', 'curp']);
const NUMERIC_CONTROLS = new Set(['number', 'currency']);
const SCALAR_CONTROLS = new Set(['textarea', 'number', 'currency', 'date', 'datetime']);

const inputBaseStyle = {
  background: colors.canvas,
  border: `1.5px solid ${colors.border}`,
  color: colors.ink,
} as const;

/** Map a text-like control type to its native HTML input `type`. */
function textInputType(controlType: string): 'email' | 'tel' | 'text' {
  if (controlType === 'email') return 'email';
  if (controlType === 'phone') return 'tel';
  return 'text';
}

/**
 * File / signature field: two-phase upload (presign → PUT → confirm). Owns its
 * own upload progress + abort state so the parent stays a thin dispatcher.
 * Label / rejection / error markup are computed by the parent and passed in.
 */
function FileUploadField({
  field,
  value,
  enabled,
  id,
  onChange,
  onUploadFile,
  onSaveDocumentExpiry,
  documentExpiry,
  labelMarkup,
  rejectionMarkup,
  errorMarkup,
}: {
  readonly field: FormFieldDef;
  readonly value: unknown;
  readonly enabled: boolean;
  readonly id: string;
  readonly onChange: (value: unknown) => void;
  readonly onUploadFile?: NonNullable<Props['onUploadFile']>;
  readonly onSaveDocumentExpiry?: NonNullable<Props['onSaveDocumentExpiry']>;
  readonly documentExpiry?: ExpirySavedState;
  readonly labelMarkup: ReactNode;
  readonly rejectionMarkup: ReactNode;
  readonly errorMarkup: ReactNode;
}): JSX.Element {
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadLoaded, setUploadLoaded] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadJustCompleted, setUploadJustCompleted] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const pointer = value as UploadedFilePointer | null;
  const isUploading = uploadProgress !== null && uploadProgress < 1;

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file || !onUploadFile) return;
    setUploadError(null);
    setUploadProgress(0);
    setUploadLoaded(0);
    setUploadTotal(file.size);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const result = await onUploadFile(file, {
        signal: controller.signal,
        onProgress: ({ fraction, loaded, total }) => {
          setUploadProgress(fraction);
          setUploadLoaded(loaded);
          setUploadTotal(total);
        },
      });
      // 600 ms green confirmation flash before clearing progress UI.
      setUploadProgress(1);
      setUploadJustCompleted(true);
      onChange(result);
      globalThis.setTimeout(() => setUploadJustCompleted(false), 600);
    } catch (err: unknown) {
      if (
        axios.isCancel(err)
        || (err as { name?: string })?.name === 'CanceledError'
        || (err as { name?: string })?.name === 'AbortError'
      ) {
        setUploadError('Upload cancelled. Pick a file again to retry.');
      } else {
        setUploadError(getApiErrorMessage(err, 'Upload failed.'));
      }
      setUploadProgress(null);
    } finally {
      abortRef.current = null;
      // Reset the input so picking the SAME file again still fires onChange.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCancel = (): void => {
    abortRef.current?.abort();
  };

  const renderBody = (): JSX.Element => {
    if (pointer) {
      const successFlash = uploadJustCompleted;
      return (
        <div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <div className="min-w-0">
              <div className="font-medium transition-colors" style={{ color: colors.success }}>
                {successFlash ? '✓ Submitted!' : '✓ Uploaded'}
              </div>
              <div className="text-xs truncate font-mono" style={{ color: colors.muted }}>
                {(pointer.size_bytes / 1024).toFixed(1)} KB · {pointer.mime_type}
              </div>
            </div>
            {enabled && !successFlash && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-xs font-semibold underline"
                style={{ color: colors.gold }}
              >
                Replace
              </button>
            )}
          </div>
          {/* Expiry capture: `file` fields only, and only when the parent
              wired the closure (onSaveDocumentExpiry is the opt-in). */}
          {field.control_type === 'file' && onSaveDocumentExpiry && (
            <DocumentExpiryCapture
              documentId={pointer.document_id}
              fieldKey={field.field_key}
              initial={documentExpiry ?? { expires_at: null, expiry_granularity: null }}
              enabled={enabled}
              onSave={onSaveDocumentExpiry}
            />
          )}
        </div>
      );
    }
    if (isUploading) {
      const pctRounded = Math.round((uploadProgress ?? 0) * 100);
      const remaining = Math.max(0, uploadTotal - uploadLoaded);
      return (
        <div className="text-sm" style={{ color: colors.inkSoft }}>
          <div className="flex items-center justify-between gap-2">
            <span>
              Uploading… {pctRounded}%
              {uploadTotal > 0 && (
                <span style={{ color: colors.muted }}>
                  {' · '}{formatBytes(remaining)} remaining
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={handleCancel}
              className="text-xs font-semibold underline"
              style={{ color: colors.danger }}
            >
              Cancel
            </button>
          </div>
          <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: colors.border }}>
            <div
              className="h-full transition-all"
              style={{ background: colors.gold, width: `${(uploadProgress ?? 0) * 100}%` }}
            />
          </div>
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={!enabled || !onUploadFile}
        className="text-sm font-semibold"
        style={{ color: enabled ? colors.gold : colors.muted, cursor: enabled ? 'pointer' : 'not-allowed' }}
      >
        Choose a file…
      </button>
    );
  };

  return (
    <label htmlFor={id} className="block">
      {labelMarkup}
      {rejectionMarkup}
      <div className="rounded-xl p-3" style={{ background: colors.canvas, border: `1.5px dashed ${colors.border}` }}>
        {renderBody()}
        <input
          ref={fileInputRef}
          id={id}
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          onChange={handleFileSelect}
          disabled={!enabled || isUploading}
          className="hidden"
        />
      </div>
      {uploadError && (
        <p className="mt-1 text-xs" style={{ color: colors.danger }}>{uploadError}</p>
      )}
      {errorMarkup}
    </label>
  );
}

/** Cascading-select options (#geo): lazy-fetch /catalogs/{slug}/items by the
 *  parent field's value; clears a stale child selection when the parent
 *  changes. Returns isCascading so the render can branch on it. */
function useCascadingOptions(
  field: FormFieldDef,
  parentValue: unknown,
  value: unknown,
  onChange: (v: unknown) => void,
): {
  isCascading: boolean;
  cascadeOptions: CatalogOption[];
  cascadeLoading: boolean;
  cascadeError: string | null;
  retryCascade: () => void;
} {
  const isCascading =
    field.control_type === 'select' &&
    !!field.options_filter_field &&
    !!field.options_catalog_slug;
  const [cascadeOptions, setCascadeOptions] = useState<CatalogOption[]>([]);
  const [cascadeLoading, setCascadeLoading] = useState(false);
  const [cascadeError, setCascadeError] = useState<string | null>(null);
  // Bumping this refetches the options ("Try again").
  const [cascadeAttempt, setCascadeAttempt] = useState(0);
  const prevParentRef = useRef<unknown>(parentValue);

  useEffect(() => {
    if (!isCascading) return;
    const slug = field.options_catalog_slug as string;
    const parentCode = parentValue == null || parentValue === '' ? null : toText(parentValue);
    // Clear stale selection when the parent changes after first render.
    if (prevParentRef.current !== parentValue) {
      if (value != null && value !== '') onChange(null);
      prevParentRef.current = parentValue;
    }
    setCascadeError(null);
    if (parentCode === null) {
      setCascadeOptions([]);
      return;
    }
    let cancelled = false;
    setCascadeLoading(true);
    workflowService
      .listCatalogItems(slug, parentCode)
      .then((items) => {
        if (cancelled) return;
        setCascadeOptions(
          items.map((i) => ({
            code: i.code,
            label: i.label,
            metadata: i.metadata as Record<string, unknown> | null | undefined,
          })),
        );
      })
      .catch((err: unknown) => {
        // An empty list used to be the only sign of failure, so a required
        // dependent field inside onboarding became a dead end with no reason.
        if (!cancelled) {
          setCascadeOptions([]);
          setCascadeError(getApiErrorMessage(err, 'Could not load the options for this field.'));
        }
      })
      .finally(() => {
        if (!cancelled) setCascadeLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // value/onChange intentionally omitted — only parent changes refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCascading, field.options_catalog_slug, parentValue, cascadeAttempt]);

  return {
    isCascading,
    cascadeOptions,
    cascadeLoading,
    cascadeError,
    retryCascade: () => setCascadeAttempt((n) => n + 1),
  };
}

/** Text-like input (text/phone/email/rfc/curp). Switches to a select when the
 *  parent supplies an optionsOverride (e.g. the SEPOMEX colonias list). */
function TextLikeField({
  field,
  value,
  enabled,
  readOnly,
  optionsOverride,
  onChange,
  handleText,
  handleBlur,
  handleCommit,
  id,
  labelMarkup,
  rejectionMarkup,
  errorMarkup,
}: {
  readonly field: FormFieldDef;
  readonly value: unknown;
  readonly enabled: boolean;
  readonly readOnly: boolean;
  readonly optionsOverride?: string[];
  readonly onChange: (value: unknown) => void;
  readonly handleText: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  readonly handleBlur: () => void;
  readonly handleCommit: () => void;
  readonly id: string;
  readonly labelMarkup: ReactNode;
  readonly rejectionMarkup: ReactNode;
  readonly errorMarkup: ReactNode;
}): JSX.Element {
  // When the parent provides a colonias list via optionsOverride, switch a
  // text field to a select so the user picks instead of types.
  if (optionsOverride && optionsOverride.length > 0) {
    return (
      <label htmlFor={id} className="block">
        {labelMarkup}
        {rejectionMarkup}
        <select
          id={id}
          value={value == null ? '' : toText(value)}
          onChange={(e) => {
            onChange(e.target.value === '' ? null : e.target.value);
            handleCommit();
          }}
          onBlur={handleBlur}
          disabled={!enabled}
          className="w-full px-3 py-2.5 text-sm rounded-xl outline-none transition-all"
          style={inputBaseStyle}
        >
          <option value="">— Select your neighborhood —</option>
          {optionsOverride.map((col) => (
            <option key={col} value={col}>{col}</option>
          ))}
        </select>
        {errorMarkup}
      </label>
    );
  }

  const isCurp = field.control_type === 'curp';
  const curpLength = isCurp ? toText(value).length : 0;
  const inputType = textInputType(field.control_type);

  return (
    <label htmlFor={id} className="block">
      {labelMarkup}
      {readOnly && (
        <span
          className="inline-flex items-center gap-1 mb-1 text-[11px] font-medium px-1.5 py-0.5 rounded"
          style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
        >
          Auto-filled
        </span>
      )}
      {rejectionMarkup}
      <input
        id={id}
        type={inputType}
        value={value == null ? '' : toText(value)}
        onChange={handleText}
        onBlur={handleBlur}
        disabled={!enabled}
        readOnly={readOnly}
        maxLength={field.max_length ?? undefined}
        placeholder={field.placeholder ?? undefined}
        className="w-full px-3 py-2.5 text-sm rounded-xl outline-none transition-all"
        style={{
          ...inputBaseStyle,
          ...(readOnly ? { background: colors.surface, cursor: 'default' } : {}),
        }}
      />
      {isCurp && (
        <div className="flex justify-end mt-1">
          <span className="text-[11px]" style={{ color: curpLength === 18 ? colors.success : colors.muted }}>
            {curpLength}/18
          </span>
        </div>
      )}
      {errorMarkup}
    </label>
  );
}

/** The field's label row: name + required star / approved-lock badge / tooltip. */
function FieldLabel({
  field,
  required,
  lockedByReview,
}: {
  readonly field: FormFieldDef;
  readonly required: boolean;
  readonly lockedByReview: boolean;
}): JSX.Element {
  return (
    <span className="block mb-1.5 text-xs font-semibold uppercase tracking-wide" style={{ color: colors.inkSoft }}>
      {field.label}
      {required && !lockedByReview && (
        <span aria-label="required" style={{ color: colors.danger }}> *</span>
      )}
      {lockedByReview && (
        <span
          className="inline-flex items-center gap-1 ml-2 normal-case font-normal text-[11px]"
          style={{ color: colors.success }}
          title="Approved by your Account Executive — this value is locked."
        >
          <span aria-hidden="true">🔒</span>{/* */}
          Approved
        </span>
      )}
      {field.tooltip && (
        <span className="block mt-0.5 text-[11px] font-normal normal-case tracking-normal" style={{ color: colors.muted }}>
          {field.tooltip}
        </span>
      )}
    </span>
  );
}

/** Inline rejection reason (staff-curated, vetted by the privacy gate). */
function RejectionAlert({
  reviewStatus,
  rejectionReason,
}: {
  readonly reviewStatus?: 'pending' | 'approved' | 'rejected';
  readonly rejectionReason?: string | null;
}): JSX.Element | null {
  if (reviewStatus !== 'rejected' || !rejectionReason) return null;
  return (
    <p
      className="text-xs mb-1.5 px-2 py-1.5 rounded"
      style={{ color: colors.danger, background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}` }}
      role="alert"
    >
      <strong>Rejected:</strong> {rejectionReason}
    </p>
  );
}

/** Validation message, shown only after the user has interacted. */
function FieldError({
  error,
  wasTouched,
}: {
  readonly error: string | null;
  readonly wasTouched: boolean;
}): JSX.Element | null {
  if (!error || !wasTouched) return null;
  return <p className="mt-1 text-xs" style={{ color: colors.danger }}>{error}</p>;
}

/** Label-wrapped scalar inputs: textarea, number/currency, date/datetime. */
function ScalarInputField({
  field,
  value,
  enabled,
  required,
  id,
  handleText,
  handleNumber,
  handleBlur,
  labelMarkup,
  rejectionMarkup,
  errorMarkup,
}: {
  readonly field: FormFieldDef;
  readonly value: unknown;
  readonly enabled: boolean;
  readonly required: boolean;
  readonly id: string;
  readonly handleText: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  readonly handleNumber: (e: ChangeEvent<HTMLInputElement>) => void;
  readonly handleBlur: () => void;
  readonly labelMarkup: ReactNode;
  readonly rejectionMarkup: ReactNode;
  readonly errorMarkup: ReactNode;
}): JSX.Element {
  const stringValue = value == null ? '' : toText(value);
  if (field.control_type === 'textarea') {
    return (
      <label htmlFor={id} className="block">
        {labelMarkup}
        {rejectionMarkup}
        <textarea
          id={id}
          rows={4}
          value={stringValue}
          onChange={handleText}
          onBlur={handleBlur}
          disabled={!enabled}
          maxLength={field.max_length ?? undefined}
          placeholder={field.placeholder ?? undefined}
          className="w-full px-3 py-2.5 text-sm rounded-xl outline-none transition-all"
          style={inputBaseStyle}
        />
        {errorMarkup}
      </label>
    );
  }
  if (NUMERIC_CONTROLS.has(field.control_type)) {
    return (
      <label htmlFor={id} className="block">
        {labelMarkup}
        {rejectionMarkup}
        <input
          id={id}
          type="number"
          inputMode={field.control_type === 'currency' ? 'decimal' : 'numeric'}
          value={stringValue}
          onChange={handleNumber}
          onBlur={handleBlur}
          disabled={!enabled}
          required={required}
          min={field.min_value ?? undefined}
          max={field.max_value ?? undefined}
          step={field.control_type === 'currency' ? '0.01' : '1'}
          placeholder={field.placeholder ?? undefined}
          className="w-full px-3 py-2.5 text-sm rounded-xl outline-none transition-all"
          style={inputBaseStyle}
        />
        {errorMarkup}
      </label>
    );
  }
  // date / datetime — birth_date is age-gated via the picker's `max`.
  const configuredMax = field.max_value == null ? undefined : String(field.max_value);
  const maxAttr =
    field.control_type === 'date' && field.field_key === 'birth_date'
      ? maxBirthDateISO()
      : configuredMax;
  const minAttr = field.min_value == null ? undefined : String(field.min_value);
  return (
    <label htmlFor={id} className="block">
      {labelMarkup}
      {rejectionMarkup}
      <input
        id={id}
        type={field.control_type === 'date' ? 'date' : 'datetime-local'}
        value={stringValue}
        onChange={handleText}
        onBlur={handleBlur}
        min={minAttr}
        max={maxAttr}
        disabled={!enabled}
        className="w-full px-3 py-2.5 text-sm rounded-xl outline-none transition-all"
        style={inputBaseStyle}
      />
      {errorMarkup}
    </label>
  );
}

/** Single-select (with cascading-options support). */
function SelectField({
  field,
  value,
  enabled,
  id,
  isCascading,
  cascadeOptions,
  cascadeLoading,
  cascadeError,
  onRetryCascade,
  parentValue,
  onChange,
  handleCommit,
  handleBlur,
  labelMarkup,
  rejectionMarkup,
  errorMarkup,
}: {
  readonly field: FormFieldDef;
  readonly value: unknown;
  readonly enabled: boolean;
  readonly id: string;
  readonly isCascading: boolean;
  readonly cascadeOptions: CatalogOption[];
  readonly cascadeLoading: boolean;
  readonly cascadeError: string | null;
  readonly onRetryCascade: () => void;
  readonly parentValue: unknown;
  readonly onChange: (value: unknown) => void;
  readonly handleCommit: () => void;
  readonly handleBlur: () => void;
  readonly labelMarkup: ReactNode;
  readonly rejectionMarkup: ReactNode;
  readonly errorMarkup: ReactNode;
}): JSX.Element {
  // Listed by the label the person reads, in their language's order, not by the English one.
  const options = (isCascading ? cascadeOptions : toCatalogOptions(field))
    .map((opt) => ({ code: opt.code, label: optionLabel(field, opt.label) }))
    .sort((a, b) => a.label.localeCompare(b.label, readerLocale()));
  const parentMissing = isCascading && (parentValue == null || parentValue === '');
  let placeholder: string;
  if (parentMissing) placeholder = '— Select the parent field first —';
  else if (cascadeLoading) placeholder = '— Loading… —';
  else if (cascadeError) placeholder = '— Options unavailable —';
  else placeholder = '— Select —';
  return (
    <label htmlFor={id} className="block">
      {labelMarkup}
      {rejectionMarkup}
      <select
        id={id}
        value={value == null ? '' : toText(value)}
        onChange={(e) => {
          onChange(e.target.value === '' ? null : e.target.value);
          handleCommit();
        }}
        onBlur={handleBlur}
        disabled={!enabled || parentMissing || cascadeLoading}
        className="w-full px-3 py-2.5 text-sm rounded-xl outline-none transition-all"
        style={inputBaseStyle}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.code} value={opt.code}>
            {opt.label}
          </option>
        ))}
      </select>
      <CascadeLoadError message={cascadeError} onRetry={onRetryCascade} />
      {errorMarkup}
    </label>
  );
}

/** The catalog whose items are countries: a field fed by it is picked with the country picker.
 *  A nationality is not a country ("Spanish", not "Spain"), so the `nationalities` catalog is NOT
 *  here: it stays a plain select of the catalog's own labels (Mexican, American, ...). */
const COUNTRY_CATALOGS = new Set(['countries']);

/** A country select: the portal's one picker, offering the catalog's own countries (the field's
 *  allowed set is the catalog, never widened here) by name. */
function CountrySelectField({
  field,
  value,
  enabled,
  id,
  onChange,
  handleCommit,
  handleBlur,
  labelMarkup,
  rejectionMarkup,
  errorMarkup,
}: {
  readonly field: FormFieldDef;
  readonly value: unknown;
  readonly enabled: boolean;
  readonly id: string;
  readonly onChange: (value: unknown) => void;
  readonly handleCommit: () => void;
  readonly handleBlur: () => void;
  readonly labelMarkup: ReactNode;
  readonly rejectionMarkup: ReactNode;
  readonly errorMarkup: ReactNode;
}): JSX.Element {
  const { codes, labels } = useMemo(() => {
    const items = toCatalogOptions(field);
    return {
      codes: items.map((item) => item.code),
      labels: Object.fromEntries(items.map((item) => [item.code, item.label])),
    };
  }, [field]);
  return (
    <div className="block">
      <label htmlFor={id} className="block">{labelMarkup}</label>
      {rejectionMarkup}
      <CountryPicker
        id={id}
        value={typeof value === 'string' && value !== '' ? value : null}
        onChange={(code) => {
          onChange(code);
          handleCommit();
        }}
        onBlur={handleBlur}
        disabled={!enabled}
        codes={codes}
        labels={labels}
      />
      {errorMarkup}
    </div>
  );
}

/** Shown under a dependent select whose options could not be loaded, so an empty
 *  list is not an unexplained dead end in the middle of onboarding. */
function CascadeLoadError({
  message,
  onRetry,
}: Readonly<{ message: string | null; onRetry: () => void }>): JSX.Element | null {
  if (!message) return null;
  return (
    <span role="alert" className="block mt-1 text-xs" style={{ color: colors.danger }}>
      {message}{' '}
      <button
        type="button"
        onClick={onRetry}
        className="underline"
        style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer' }}
      >
        Try again
      </button>
    </span>
  );
}

/** Radio group (own legend so the required/approved/tooltip affordances match). */
function RadioField({
  field,
  value,
  enabled,
  required,
  lockedByReview,
  id,
  onChange,
  handleCommit,
  rejectionMarkup,
  errorMarkup,
}: {
  readonly field: FormFieldDef;
  readonly value: unknown;
  readonly enabled: boolean;
  readonly required: boolean;
  readonly lockedByReview: boolean;
  readonly id: string;
  readonly onChange: (value: unknown) => void;
  readonly handleCommit: () => void;
  readonly rejectionMarkup: ReactNode;
  readonly errorMarkup: ReactNode;
}): JSX.Element {
  const options = toCatalogOptions(field);
  return (
    <fieldset className="block">
      <legend className="block mb-2 text-xs font-semibold uppercase tracking-wide" style={{ color: colors.inkSoft }}>
        {field.label}
        {required && !lockedByReview && <span aria-label="required" style={{ color: colors.danger }}> *</span>}
        {lockedByReview && (
          <span
            className="inline-flex items-center gap-1 ml-2 normal-case font-normal text-[11px]"
            style={{ color: colors.success }}
            title="Approved by your Account Executive — this value is locked."
          >
            <span aria-hidden="true">🔒</span>{/* */}
            Approved
          </span>
        )}
        {field.tooltip && (
          <span className="block mt-0.5 text-[11px] font-normal normal-case tracking-normal" style={{ color: colors.muted }}>
            {field.tooltip}
          </span>
        )}
      </legend>
      {rejectionMarkup}
      <div className="space-y-2">
        {options.map((opt) => {
          const optionId = `${id}-${opt.code}`;
          return (
            <label key={opt.code} htmlFor={optionId} className="flex items-start gap-2 cursor-pointer">
              <input
                id={optionId}
                type="radio"
                name={field.field_key}
                value={opt.code}
                checked={value === opt.code}
                onChange={() => {
                  onChange(opt.code);
                  handleCommit();
                }}
                disabled={!enabled}
                className="mt-1"
              />
              <span className="text-sm" style={{ color: colors.ink }}>{opt.label}</span>
            </label>
          );
        })}
      </div>
      {errorMarkup}
    </fieldset>
  );
}

/** Single boolean checkbox. */
function CheckboxField({
  field,
  value,
  enabled,
  required,
  id,
  onChange,
  handleCommit,
  errorMarkup,
}: {
  readonly field: FormFieldDef;
  readonly value: unknown;
  readonly enabled: boolean;
  readonly required: boolean;
  readonly id: string;
  readonly onChange: (value: unknown) => void;
  readonly handleCommit: () => void;
  readonly errorMarkup: ReactNode;
}): JSX.Element {
  return (
    <label htmlFor={id} className="flex items-start gap-2 cursor-pointer">
      <input
        id={id}
        type="checkbox"
        checked={value === true}
        onChange={(e) => {
          onChange(e.target.checked ? true : null);
          handleCommit();
        }}
        disabled={!enabled}
        className="mt-1"
      />
      <span className="text-sm" style={{ color: colors.ink }}>
        {field.label}
        {required && <span aria-label="required" style={{ color: colors.danger }}> *</span>}
        {field.tooltip && (
          <span className="block mt-0.5 text-[11px]" style={{ color: colors.muted }}>
            {field.tooltip}
          </span>
        )}
      </span>
      {errorMarkup}
    </label>
  );
}

/**
 * One workflow form field rendered as a native HTML input.
 *
 * Supports all 15 workflow control types including `file` (two-phase
 * presign → PUT → confirm upload) and `signature`. `multiselect` and
 * `address` are not yet supported and render as a warning placeholder.
 *
 * The component is "controlled": value + error come from the parent
 * (which owns the case data + validation via `resolveFields()`).
 */
// nosemgrep: nodejs_scan.javascript-dos-rule-regex_dos
const DynamicField = ({
  field,
  value,
  parentValue,
  enabled: enabledFromForm,
  required,
  error,
  wasTouched = false,
  onTouch,
  reviewStatus,
  rejectionReason,
  onChange,
  onUploadFile,
  documentExpiry,
  onSaveDocumentExpiry,
  readOnly = false,
  optionsOverride,
}: Props): JSX.Element => {
  // Approved fields are read-only regardless of what the form
  // engine says. The agent's decision is final until the case
  // re-enters review; locking prevents the client from accidentally
  // clearing data the agent already signed off on. By shadowing the
  // `enabled` name here, every existing input that disables itself
  // on `!enabled` automatically respects the review lock too — no
  // need to touch each branch.
  const lockedByReview = reviewStatus === 'approved';
  const enabled = enabledFromForm && !lockedByReview;
  const id = `field-${field.field_key}`;
  const isUnsupported = !SUPPORTED_CONTROLS.has(field.control_type);

  // Cascading select state: lazy-fetched from
  // /catalogs/{slug}/items?parent_code=<parentValue> whenever the
  // referenced sibling field changes (see options_filter_field).
  const { isCascading, cascadeOptions, cascadeLoading, cascadeError, retryCascade } = useCascadingOptions(
    field, parentValue, value, onChange,
  );

  const labelMarkup = <FieldLabel field={field} required={required} lockedByReview={lockedByReview} />;
  const rejectionMarkup = <RejectionAlert reviewStatus={reviewStatus} rejectionReason={rejectionReason} />;
  const errorMarkup = <FieldError error={error} wasTouched={wasTouched} />;

  // Single callback all input handlers route their blur through —
  // safe to leave `undefined` from the parent; this is a no-op then.
  const handleBlur = (): void => {
    onTouch?.();
  };

  // For controls that commit on change instead of blur (radio /
  // select / checkbox), we ALSO call onTouch on change so the error
  // surfaces immediately if the choice they pick happens to be
  // invalid (shouldn't normally happen for selects, but covers it).
  const handleCommit = (): void => {
    onTouch?.();
  };

  const handleText = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    // CURP/RFC are case-insensitive identifiers whose canonical form is
    // uppercase. Upcase as the user types so what they see matches what the
    // regex validates and what the backend stores (the backend re-normalises
    // defensively — never trust the client to send the canonical form).
    const raw = e.target.value;
    const next =
      field.control_type === 'curp' || field.control_type === 'rfc'
        ? raw.toUpperCase()
        : raw;
    onChange(next === '' ? null : next);
  };

  const handleNumber = (e: ChangeEvent<HTMLInputElement>): void => {
    // Money has two decimals at most: a third one typed is dropped.
    const raw = field.control_type === 'currency' ? limitToCents(e.target.value) : e.target.value;
    if (raw === '') return onChange(null);
    const n = Number(raw);
    onChange(Number.isNaN(n) ? raw : n);
  };

  if (isUnsupported) {
    return (
      <label htmlFor={id} className="block">
        {labelMarkup}
        {rejectionMarkup}
        <div
          id={id}
          className="px-3 py-2.5 text-xs rounded-xl"
          style={{
            background: colors.warningBg,
            border: `1px dashed ${colors.warning}`,
            color: colors.warning,
          }}
        >
          {`Control type "${field.control_type}" not yet supported.`}
        </div>
      </label>
    );
  }

  // ── File / signature ─────────────────────────────────────────
  // Two-phase upload (presign → PUT direct to bucket → confirm).
  // The parent supplies `onUploadFile` so this component stays free
  // of service-layer concerns. `value` is the JSON pointer the
  // backend persists in case_data after a successful confirm.
  if (FILE_CONTROLS.has(field.control_type)) {
    return (
      <FileUploadField
        field={field}
        value={value}
        enabled={enabled}
        id={id}
        onChange={onChange}
        onUploadFile={onUploadFile}
        onSaveDocumentExpiry={onSaveDocumentExpiry}
        documentExpiry={documentExpiry}
        labelMarkup={labelMarkup}
        rejectionMarkup={rejectionMarkup}
        errorMarkup={errorMarkup}
      />
    );
  }

  // ── Text-like ────────────────────────────────────────────────
  if (TEXT_CONTROLS.has(field.control_type)) {
    return (
      <TextLikeField
        field={field}
        value={value}
        enabled={enabled}
        readOnly={readOnly}
        optionsOverride={optionsOverride}
        onChange={onChange}
        handleText={handleText}
        handleBlur={handleBlur}
        handleCommit={handleCommit}
        id={id}
        labelMarkup={labelMarkup}
        rejectionMarkup={rejectionMarkup}
        errorMarkup={errorMarkup}
      />
    );
  }

  if (SCALAR_CONTROLS.has(field.control_type)) {
    return (
      <ScalarInputField
        field={field}
        value={value}
        enabled={enabled}
        required={required}
        id={id}
        handleText={handleText}
        handleNumber={handleNumber}
        handleBlur={handleBlur}
        labelMarkup={labelMarkup}
        rejectionMarkup={rejectionMarkup}
        errorMarkup={errorMarkup}
      />
    );
  }

  if (field.control_type === 'select' && COUNTRY_CATALOGS.has(field.options_catalog_slug ?? '')) {
    return (
      <CountrySelectField
        field={field}
        value={value}
        enabled={enabled}
        id={id}
        onChange={onChange}
        handleCommit={handleCommit}
        handleBlur={handleBlur}
        labelMarkup={labelMarkup}
        rejectionMarkup={rejectionMarkup}
        errorMarkup={errorMarkup}
      />
    );
  }

  if (field.control_type === 'select') {
    return (
      <SelectField
        field={field}
        value={value}
        enabled={enabled}
        id={id}
        isCascading={isCascading}
        cascadeOptions={cascadeOptions}
        cascadeLoading={cascadeLoading}
        cascadeError={cascadeError}
        onRetryCascade={retryCascade}
        parentValue={parentValue}
        onChange={onChange}
        handleCommit={handleCommit}
        handleBlur={handleBlur}
        labelMarkup={labelMarkup}
        rejectionMarkup={rejectionMarkup}
        errorMarkup={errorMarkup}
      />
    );
  }

  if (field.control_type === 'radio') {
    return (
      <RadioField
        field={field}
        value={value}
        enabled={enabled}
        required={required}
        lockedByReview={lockedByReview}
        id={id}
        onChange={onChange}
        handleCommit={handleCommit}
        rejectionMarkup={rejectionMarkup}
        errorMarkup={errorMarkup}
      />
    );
  }

  if (field.control_type === 'checkbox') {
    return (
      <CheckboxField
        field={field}
        value={value}
        enabled={enabled}
        required={required}
        id={id}
        onChange={onChange}
        handleCommit={handleCommit}
        errorMarkup={errorMarkup}
      />
    );
  }

  // Should be unreachable given SUPPORTED_CONTROLS check above.
  return <div />;
};

/** Normalize inline + catalog options into a uniform `{code,label}` list.
 *  Inline options come back as `{label,value}` from the seed; catalog
 *  options come as `{code,label,metadata}`. */
function toCatalogOptions(field: FormFieldDef): CatalogOption[] {
  if (!field.options) return [];
  if (field.options_source === 'catalog') {
    return field.options as CatalogOption[];
  }
  // Inline: `{label, value}` shape per the seed convention.
  return (field.options as Array<{ label: string; value: unknown }>).map((o) => ({
    code: String(o.value),
    label: o.label,
  }));
}

export default DynamicField;
